import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './App.css';
import Loader from './components/Loader';
import AudioPlayer from './components/AudioPlayer';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import bgMusic from './assets/sounds/bg.mp3';
import MainContent from './components/MainContent';

// Split-opening storyboard
//    0ms  Loader completes; burgundy frame holds
//    0ms  Top and bottom panels begin moving apart
//  350ms  Live Hero mounts behind the panels and prepares for two frames
// ~382ms  Background cross-fades; petals and name begin entering
// 1200ms  Panels clear without mounting additional animation systems
// 1700ms  Deferred below-fold content mounts after the Hero reveal settles
const SPLIT_TIMING = {
  duration: 1.2,
  heroMountDelayMs: 350,
  backgroundFadeMs: 300,
  secondaryMountDelayMs: 500
};

const SPLIT_TRANSITION = {
  type: 'tween',
  duration: SPLIT_TIMING.duration,
  ease: [0.645, 0.045, 0.355, 1]
};

export const IntroContext = React.createContext({ introStage: 5, isIntroDone: true });

function App() {
  const [introStage, setIntroStage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (introStage < 5) return undefined;

    const audio = new Audio(bgMusic);
    let disposed = false;

    audio.autoplay = true;
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = 0.5;
    audio.playsInline = true;
    audioRef.current = audio;

    const attemptAutoplay = async () => {
      if (disposed || !audio.paused) return;

      try {
        await audio.play();
      } catch (error) {
        setIsPlaying(false);

        if (error.name !== 'NotAllowedError') {
          console.error('Automatic playback failed:', error);
        }
      }
    };

    const handlePlaying = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        attemptAutoplay();
      }
    };

    audio.addEventListener('canplay', attemptAutoplay, { once: true });
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', attemptAutoplay);

    audio.load();
    attemptAutoplay();

    return () => {
      disposed = true;
      audio.removeEventListener('canplay', attemptAutoplay);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', attemptAutoplay);
      audio.pause();

      if (audioRef.current === audio) {
        audioRef.current = null;
      }
    };
  }, [introStage]);

  useEffect(() => {
    if (introStage !== 4) return undefined;

    const timer = setTimeout(
      () => setIntroStage(5),
      SPLIT_TIMING.secondaryMountDelayMs
    );

    return () => clearTimeout(timer);
  }, [introStage]);

  useEffect(() => {
    if (introStage !== 1) return undefined;

    const timer = setTimeout(
      () => setIntroStage(2),
      SPLIT_TIMING.heroMountDelayMs
    );

    return () => clearTimeout(timer);
  }, [introStage]);

  useEffect(() => {
    if (introStage !== 2) return undefined;

    let readyFrame;
    const mountedFrame = requestAnimationFrame(() => {
      readyFrame = requestAnimationFrame(() => setIntroStage(3));
    });

    return () => {
      cancelAnimationFrame(mountedFrame);
      if (readyFrame) cancelAnimationFrame(readyFrame);
    };
  }, [introStage]);

  const handleLoadingComplete = useCallback(() => {
    setIntroStage(reduceMotion ? 5 : 1);
  }, [reduceMotion]);

  const handleSplitComplete = useCallback(() => {
    setIntroStage(4);
  }, []);

  return (
    <IntroContext.Provider value={{ introStage, isIntroDone: introStage >= 3 }}>
      <div className="app-container">
        {introStage < 4 && (
          <div
            className={`intro-background${introStage >= 3 ? ' is-hiding' : ''}`}
            aria-hidden="true"
            style={{
              '--intro-background-desktop': `url("${process.env.PUBLIC_URL}/assets/herobg.png")`,
              '--intro-background-mobile': `url("${process.env.PUBLIC_URL}/assets/herobg phoen.png")`,
              '--intro-background-fade': `${SPLIT_TIMING.backgroundFadeMs}ms`,
            }}
          />
        )}
        <div className="grid-overlay"></div>
        
        <AudioPlayer 
          isPlaying={isPlaying} 
          setIsPlaying={setIsPlaying}
          audioRef={audioRef}
        />
        {introStage >= 2 && (
          <Router>
            <Routes>
              <Route
                path="/"
                element={<MainContent showSecondary={introStage >= 5} />}
              />
            </Routes>
          </Router>
        )}
      {introStage < 4 && (
        <div className="loader-container">
          {introStage === 0 && (
            <Loader onLoadingComplete={handleLoadingComplete} />
          )}
          {introStage >= 1 && (
            <div className="transition-container">
              <motion.div
                className="transition-panel top"
                initial={{ y: '0%' }}
                animate={{ y: '-100%' }}
                transition={SPLIT_TRANSITION}
                onAnimationComplete={handleSplitComplete}
              >
                <div className="panel-content"></div>
              </motion.div>
              <motion.div
                className="transition-panel bottom"
                initial={{ y: '0%' }}
                animate={{ y: '100%' }}
                transition={SPLIT_TRANSITION}
              >
                <div className="panel-content"></div>
              </motion.div>
            </div>
          )}
        </div>
      )}
      </div>
    </IntroContext.Provider>
  );
}

export default App;
